/**
 * Beat 9, part 2 — the shed at night, with nothing in it that wants anything.
 *
 * `docs/beat-9-plan.md` is unusually specific about this room, because it is the
 * one scene in Act 2 that can be ruined by adding something good to it:
 *
 * > the rest state contains **nothing to complete**. Storm gone; no watcher; no
 * > timer; no objective; no interactable; no scripted exit; no companion
 * > command. […] That absence is content.
 *
 * So this module is deliberately the smallest story file in the project: one
 * authored position, one flag, and two stages whose only job is to fade in, wait
 * for the player to decide, and fade out.
 *
 * **Holly is a frozen snapshot and not a `Companion`, and that is the whole
 * design.** Part 1's companion is a controller: it has a standoff, obstacles,
 * stations and a step function, and any of those could move her. Part 2 does not
 * construct one, so "nothing advances while the player stands still" is true
 * because there is nothing here that could advance it — not because a test says
 * so. Beat 10 can build a real `Companion` from this constant on the day she has
 * somewhere to go.
 */

import type { CompanionSnapshot } from '@/systems/companion';
import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Where she is asleep: in the straw bedding at the west end, broadside.
 *
 * Three constraints picked this and not one of them is arbitrary. She has to be
 * **visible from the authored spawn** (which part 2 shares with part 1, so the
 * closed/open door delta in `wool-shed.spec.ts` keeps feeding both parts the
 * same input); she has to be **broadside** to it, because she is 1.24 m of pale
 * flank and that is the only silhouette this animal has; and she has to be
 * **under the lamp**, which `woolShedFixtures` hangs over this bedding rather
 * than over the door. That last one is the whole scene: the two of them in a
 * warm pool at the west end, and the way out a cold rectangle five metres away
 * that nothing is pushing them toward.
 *
 * 1.45 m off the west gable and 1.6 m clear of `king-post-west`, which is the
 * nearest thing she could be standing inside.
 */
export const HOLLY_RESTING: CompanionSnapshot = {
  x: -3.05,
  z: 0.80,
  yaw: 1.75,
  phase: 'resting',
  moving: false,
  travelledDistance: 0,
};

export const WOOL_SHED_REST_FLAGS = {
  /** Raised by the room when she crosses the north wall plane. Nothing else raises it. */
  leftTheShed: 'wool-shed-rest.leftTheShed',
} as const;

export const WOOL_SHED_REST_CUES = {
  open: 'wool-shed-rest.open',
  fadeOut: 'wool-shed-rest.fadeOut',
  end: 'wool-shed-rest.end',
} as const;

const FADE_SECONDS = 4;

/**
 * The one stage in this game that has no patience, and it is not an oversight.
 *
 * `blackoutHall.ts` states the rule it is breaking: *"Every stage has a patience
 * […] because an Act 2 boot that cannot finish is a save the player cannot get
 * out of."* That rule is about stages that wait for the player to notice
 * something. This one waits for the player to be **finished** — and a rest state
 * that ends itself after ninety seconds is a cutscene with a walk in it, which
 * is precisely the thing S-8 exists to refuse.
 *
 * It costs nothing a backstop would buy: the doorway is standing open two metres
 * from the spawn's line of sight, there is nothing to solve, and the pause menu
 * restarts.
 */
function restStage(): Stage {
  return {
    id: 'rest',
    enter: (_ctx, emit): void => emit(WOOL_SHED_REST_CUES.open),
    done: (ctx: StoryContext): boolean => ctx.completed.has(WOOL_SHED_REST_FLAGS.leftTheShed),
  };
}

export const WOOL_SHED_REST_BEAT: Beat = {
  number: 9,
  id: 'act2.woolShedRest',
  stages: [
    restStage(),
    {
      id: 'close',
      enter: (_ctx, emit): void => emit(WOOL_SHED_REST_CUES.fadeOut),
      done: (ctx: StoryContext): boolean => ctx.stageTime >= FADE_SECONDS,
      exit: (_ctx, emit): void => emit(WOOL_SHED_REST_CUES.end),
    },
  ],
};
